import { Map } from "@mui/icons-material"
import { Box, Button, FormControl, Grid2 as Grid, InputLabel, MenuItem, Paper, Select } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import CustomToolbar from "../components/CustomToolbar"
import DialogConfirm from "../components/DialogConfirm"
import DialogForm from "../components/DialogForm"
import { ACLuogo } from "../components/forms/autocompletes"
import LuogoForm from "../components/forms/LuogoForm"
import IconButtonDelete from "../components/IconButtonDelete"
import IconButtonEdit from "../components/IconButtonEdit"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import { useQsFilters } from "../hooks/filters"
import { useLuoghiList, useLuogoCreate, useLuogoDelete, useLuogoUpdate } from "../hooks/luoghi"
import useUserCanWriteAndDelete from "../hooks/permissions"
import { useSnackbarMessage } from "../hooks/snackbar"
import { Luogo } from "../types"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "nome",
  luogo_padre: params.get("luogo_padre") ?? "",
  livello_geografico: params.get("livello_geografico") ?? "",
})

export default function Luoghi() {
  const pageSize = usePageSize()
  const { filters, setFilters, setFiltersDebounced, uiFilters } = useQsFilters(initFilters)
  const { t } = useTranslation()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "nome",
        headerName: t("luogo.nome"),
        flex: 1,
      },
      {
        field: "livello_geografico",
        headerName: t("luogo.livello_geografico"),
        flex: 1,
      },
      {
        field: "luogo_padre_data.nome",
        headerName: t("luogo.luogo_padre"),
        sortable: false,
        flex: 1,
        renderCell: (params) => params.row.luogo_padre_data?.nome ?? "",
      },
      {
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButtonEdit onClick={() => editModalActions.open(params.row)} />
            {userCanWriteAndDelete && (
              <IconButtonDelete disabled={!params.row.can_delete} onClick={() => deleteModalActions.open(params.row)} />
            )}
          </>
        ),
      },
    ]
  }, [t])

  const { data, isLoading } = useLuoghiList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
    luogo_padre: filters.luogo_padre,
    livello_geografico: filters.livello_geografico,
  })

  const { mutateAsync: create } = useLuogoCreate()
  const { mutateAsync: update } = useLuogoUpdate()
  const { mutateAsync: remove } = useLuogoDelete()

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const [createModalState, createModalActions] = useModalTrigger()
  const [editModalState, editModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  const filtersCount = useMemo(() => {
    let count = 0
    if (filters.luogo_padre) count++
    if (filters.livello_geografico) count++

    return count
  }, [filters.luogo_padre, filters.livello_geografico])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PageTitle
            title={t("luogo.titolo_lista")}
            breadcrumbs={[
              {
                label: t("general.luoghi"),
                iconType: Map,
              },
            ]}
          />
          {userCanWriteAndDelete && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                createModalActions.open()
              }}
            >
              {t("luogo.cta_aggiungi")}
            </Button>
          )}
        </Box>
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() => setFilters({ search: "", page: 1, luogo_padre: "", livello_geografico: "" })}
              filtersCount={filtersCount}
              ref={toolbarRef}
            >
              <Grid container spacing={2}>
                <Grid size={3}>
                  <ACLuogo
                    size="small"
                    margin="dense"
                    variant="outlined"
                    label={t("luogo.luogo_padre")}
                    value={filters.luogo_padre ? parseInt(filters.luogo_padre) : undefined}
                    onChange={(value) => {
                      setFilters({ ...filters, luogo_padre: value ? String(value) : "" })
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <FormControl size="small" fullWidth margin="dense" variant="outlined">
                    <InputLabel>{t("luogo.livello_geografico")}</InputLabel>
                    <Select
                      fullWidth
                      value={filters.livello_geografico}
                      onChange={(e) => {
                        setFilters({ livello_geografico: e.target.value, page: 1 })
                      }}
                      label={t("luogo.livello_geografico")}
                      size="small"
                      variant="outlined"
                      margin="dense"
                    >
                      <MenuItem value={"toponimo"}>{t("luogo.enum_livello_geografico.toponimo")}</MenuItem>
                      <MenuItem value={"comune"}>{t("luogo.enum_livello_geografico.comune")}</MenuItem>
                      <MenuItem value={"provincia"}>{t("luogo.enum_livello_geografico.provincia")}</MenuItem>
                      <MenuItem value={"regione"}>{t("luogo.enum_livello_geografico.regione")}</MenuItem>
                      <MenuItem value={"nazione"}>{t("luogo.enum_livello_geografico.nazione")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CustomToolbar>
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  disableColumnResize
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator numPages={numPages} page={filters.page} setPage={(page) => setFilters({
                        ...filters,
                        page
                      })} />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<Luogo>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("luogo.cta_aggiungi")}
        FormComponent={LuogoForm}
        save={(values: Luogo) => {
          return create(values).then(() => {
            createModalActions.close()
          })
        }}
      />
      <DialogForm<Luogo>
        open={editModalState.isOpen}
        handleClose={editModalActions.close}
        title={t("luogo.cta_modifica")}
        FormComponent={LuogoForm}
        save={(values: Luogo) => {
          return update(values).then(() => {
            editModalActions.close()
            showMessage(t("luogo.esito_modifica_ok"))
          })
        }}
        item={editModalState.value}
        hasCancel
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("luogo.cta_elimina")}
        subtitle={
          <span>
            {t("luogo.cta_subtitle_elimina")} <b>{deleteModalState.value?.nome}</b> ?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("luogo.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </Layout>
  )
}
